// TimePickerDual.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Picker from 'react-mobile-picker';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import InputFormBorder from '../Common/InputFormBorder/InputFormBorder';

const pad2 = (n) => String(n).padStart(2, '0');
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const parseValue = (v) => {
  if (!v) return { hour: 0, minute: 0 };
  if (typeof v === 'string') {
    const [hStr, mStr] = v.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    return {
      hour: isNaN(h) ? 0 : clamp(h, 0, 23),
      minute: isNaN(m) ? 0 : clamp(m, 0, 59),
    };
  }
  return {
    hour: clamp(v.hour ?? 0, 0, 23),
    minute: clamp(v.minute ?? 0, 0, 59),
  };
};

const BookSelectorFixed = ({ children }) => {
  const [portalRoot, setPortalRoot] = useState(null);

  useEffect(() => {
    const element = document.getElementById('bookSelector');
    if (element) {
      setPortalRoot(element);
    }
  }, []);

  if (!portalRoot) {
    return null;
  }

  return createPortal(<div>{children}</div>, portalRoot);
};

export default function TimePickerDual({
  placeholder,
  onChange,
  minuteStep = 5,
  wheelMode = 'normal',
  className,
  disabled = false,
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [map, setMap] = useState({
    hour: pad2(0),
    minute: pad2(0),
  });
  const lastMapRef = useRef(map);

  // Sync map with selectedTime when component mounts
  useEffect(() => {
    const initialMap = selectedTime
      ? {
          hour: pad2(selectedTime.hour),
          minute: pad2(Math.floor(selectedTime.minute / minuteStep) * minuteStep),
        }
      : {
          hour: pad2(0),
          minute: pad2(0),
        };
    setMap(initialMap);
    lastMapRef.current = initialMap;
  }, [minuteStep]);

  // Opciones de horas y minutos
  const hourOptions = useMemo(() => Array.from({ length: 24 }, (_, h) => pad2(h)), []);

  const minuteOptions = useMemo(() => {
    const step = Math.max(1, Math.min(30, Math.round(minuteStep)));
    const arr = [];
    for (let m = 0; m <= 59; m += step) arr.push(pad2(m));
    return arr;
  }, [minuteStep]);

  const emit = (next) => {
    const num = { hour: parseInt(next.hour, 10), minute: parseInt(next.minute, 10) };
    const asString = `${pad2(num.hour)}:${pad2(num.minute)}`;
    setSelectedTime(num);
    onChange?.(num, asString);
  };

  const handleChange = (next) => {
    setMap(next);
    // Emite solo si realmente cambió algo
    if (next.hour !== lastMapRef.current.hour || next.minute !== lastMapRef.current.minute) {
      emit(next);
    }
    lastMapRef.current = next;
  };

  const onHandleDone = () => {
    setIsOpen(false);
  };

  return (
    <>
      <div
        className='w-100'
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(true);
        }}
      >
        <InputFormBorder
          placeholder={placeholder}
          value={
            selectedTime
              ? selectedTime.hour + ':' + selectedTime.minute.toString().padStart(2, '0')
              : ''
          }
          disableFocus={true}
          //onChange={(e) => setAirline(e.target.value)}
          //onClick={() => setIsOpen(true)}
        />
      </div>
      <BookSelectorFixed>
        <div className={`book-selector font-medium ${isOpen ? 'open' : ''}`}>
          <div
            className='font-normal text-right text-white py-2 px-3'
            onClick={() => onHandleDone()}
          >
            {t('done')}
          </div>
          <div className={className} aria-label='Time picker' aria-disabled={disabled}>
            <Picker value={map} onChange={handleChange} wheelMode={wheelMode} disabled={disabled}>
              <Picker.Column name='hour'>
                {hourOptions.map((h) => (
                  <Picker.Item key={h} value={h} aria-label={`${h} hours`}>
                    {({ selected }) => (
                      <div
                        className='text-right w-100 mr-2'
                        style={{ color: selected ? '#fff' : '#7a7676' }}
                      >
                        {h}
                      </div>
                    )}
                  </Picker.Item>
                ))}
              </Picker.Column>
              <div className='d-flex align-items-center mb-1 text-white'>:</div>
              <Picker.Column name='minute'>
                {minuteOptions.map((m) => (
                  <Picker.Item key={m} value={m} aria-label={`${m} minutes`}>
                    {({ selected }) => (
                      <div
                        className='text-left w-100 ml-2'
                        style={{ color: selected ? '#fff' : '#7a7676' }}
                      >
                        {m}
                      </div>
                    )}
                  </Picker.Item>
                ))}
              </Picker.Column>
            </Picker>
          </div>
        </div>
      </BookSelectorFixed>
    </>
  );
}
